import { useNavigate } from 'react-router-dom'

import { ErrorView, Loader } from '@percent/cause-dashboard/common/components'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  usePostHogFeatureFlag,
  useQueryList
} from '@percent/cause-dashboard/common/hooks'
import { dayJS } from '@percent/cause-dashboard/common/utility/date'
import {
  formatAmount,
  formatMinorUnitsValueToFullNumberWithDecimals
} from '@percent/cause-dashboard/common/utility/money/formatAmount'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Payout, PayoutStatus } from '@percent/cause-dashboard/services/payouts/payoutsService.types'
import {
  Badge,
  BadgeVariant,
  ButtonText,
  Flex,
  IconRegistry,
  Table,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography
} from '@percent/lemonade'
import { Trans, useTranslation } from 'react-i18next'
import { RoutePath } from '../routes/Routes'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import styles from './PayoutsPage.module.scss'

export function PayoutsPage() {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.payouts') }])

  const { payoutsService } = useServices()
  const {
    currencyInfo,
    authState: { user }
  } = useAuthState()
  const navigate = useNavigate()

  const showReturnedPayouts = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_SHOW_RETURNED_PAYOUTS)

  const [{ data, totalResults, isLoading, errorMessage }, { nextPage, previousPage }] = useQueryList(
    payoutsService.getPayoutsList(user?.organisationId as string)
  )

  const columns = [
    { id: t('typography.payoutsTableAmount'), isSortable: false, props: { width: '14%' } },
    { id: t('typography.payoutsTableStatus'), isSortable: false, props: { width: 'auto' } },
    { id: t('typography.payoutsTablePayoutDate'), isSortable: false, props: { width: '23%' } }
  ]

  const statusBadge: Record<PayoutStatus, { label: string; variant: BadgeVariant; icon: keyof typeof IconRegistry }> = {
    [PayoutStatus.PENDING]: {
      label: t('typography.pending'),
      variant: 'warning',
      icon: 'clock'
    },
    [PayoutStatus.PAID]: {
      label: t('typography.paid'),
      variant: 'positive',
      icon: 'approve'
    },
    [PayoutStatus.RETURNED]: {
      label: t('typography.paymentReturned'),
      variant: 'warning',
      icon: 'warning'
    }
  }

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Flex gap={12} direction="column" wrap="nowrap">
      <div>
        <TableProvider
          isLoading={false}
          totalResults={totalResults}
          columns={columns}
          emptyTableTitle={t('typography.noPayouts')}
          illustration="no-payouts"
        >
          {data?.length !== 0 && (
            <div className={styles.title}>
              <Typography variant="h3">{t('typography.allPayouts')}</Typography>
            </div>
          )}
          <Table>
            {data
              ?.filter(payout => (showReturnedPayouts ? true : payout.status !== PayoutStatus.RETURNED))
              .map(({ id, paidAt, paidAmount, paidCurrencyCode, status }: Payout, index: number) => (
                <TableRow key={`donation-list-${id}`} className={styles.listItem}>
                  <TableCell>
                    {paidAmount
                      ? formatAmount({
                          currencyCode: paidCurrencyCode,
                          value: formatMinorUnitsValueToFullNumberWithDecimals(
                            paidAmount,
                            currencyInfo?.find(el => el.code === paidCurrencyCode)?.minorUnits as number
                          )
                        })
                      : null}
                  </TableCell>
                  <TableCell>
                    {showReturnedPayouts ? (
                      <Tooltip
                        content={
                          status === PayoutStatus.RETURNED ? (
                            <Trans i18nKey="typography.paymentReturnedTooltip" components={{ 1: <br /> }} />
                          ) : undefined
                        }
                        lineClamp={0}
                      >
                        <span>
                          <Badge variant={statusBadge[status].variant} icon={statusBadge[status].icon}>
                            {statusBadge[status].label}
                          </Badge>
                        </span>
                      </Tooltip>
                    ) : (
                      <Badge variant="positive" icon="approve">
                        {t('typography.paid')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{dayJS(paidAt).format('DD MMM YYYY')}</TableCell>
                  <TableCell className={styles.seeDetails}>
                    <ButtonText
                      onClick={() => {
                        navigate(`${RoutePath.PAYOUTS}/${id}`)
                      }}
                    >
                      {t('typography.seeDetails')}
                    </ButtonText>
                  </TableCell>
                </TableRow>
              ))}
          </Table>
          <TablePagination
            previousPage={previousPage}
            nextPage={nextPage}
            totalResultsText={t('typography.results', { count: totalResults })}
          />
        </TableProvider>
      </div>
    </Flex>
  )
}
